/**
 * InfiniteScrolledList is a higher order component that passes to the wrapped
 * component a node called infiniteList that handles updates
 *
 * @number listProperties.listItemHeight - height of each list item element
 * @string listProperties.className - string to prefix to class names
 * @node listProperties.listItemComponent - component to pass id to and construct list
 * @function listProperties.apiCall - the call to fetch data for list
 * @object listProperties.apiCallParams - parameters to feed apiCall
}
 */
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import throttle from 'lodash/throttle';
import PropTypes from 'prop-types';
import React from 'react';
import Infinite from 'react-infinite';
import { connect } from 'react-redux';

import { clearGetOnInfiniteScrollCache } from '../../lib/InfiniteScrollUtils';

import { Loading } from './Loading/Loading';

const RANDOM_ACCESS_TIMEOUT = 200;

export const SPINNER_WHITE = 'WHITE';
export const SPINNER_BLACK = 'BLACK';

export default function InfiniteScrolledList(Component, listProperties) {
  const Wrapped = class extends React.Component {
    static propTypes = {
      accessibleAttributes: PropTypes.object,
      containerHeight: PropTypes.number,
      ctaButton: PropTypes.node,
      dispatch: PropTypes.func.isRequired,
      hasFetchedData: PropTypes.bool,
      listItemProps: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
      listItemTotal: PropTypes.number,
      onCurrentViewedIndexChange: PropTypes.func,
      onInfiniteScroll: PropTypes.func.isRequired,
      onScroll: PropTypes.func,
      receivedResultsInLastRequest: PropTypes.bool,
      spinnerTheme: PropTypes.string,
    };

    static defaultProps = {
      spinnerTheme: SPINNER_BLACK,
    };

    state = {
      isLoading: true,
      scrollTop: 0,
      firstVisibleIndex: 0,
      lastVisibleIndex: 0,
    };

    componentDidMount() {
      this.fetchData(true);
    }

    componentWillReceiveProps(nextProps) {
      if (this.state.isLoading && nextProps.hasFetchedData) {
        this.setState({
          isLoading: false,
        });
      }
      if (!this.state.isLoading && !nextProps.hasFetchedData) {
        this.setState({
          isLoading: true,
        });
      }
    }

    componentDidUpdate(prevProps, prevState) {
      if (
        this.state.firstVisibleIndex !== prevState.firstVisibleIndex ||
        this.state.lastVisibleIndex !== prevState.lastVisibleIndex
      ) {
        this.fetchData();
      }
    }

    componentWillUnmount() {
      clearGetOnInfiniteScrollCache();
    }

    fetchData = (forceFetch = false) => {
      this.props.onInfiniteScroll &&
        this.props.dispatch(
          this.props.onInfiniteScroll({
            firstVisibleIndex: this.state.firstVisibleIndex,
            lastVisibleIndex: this.state.lastVisibleIndex,
            forceFetch,
          }),
        );
    };

    renderListItems = () => {
      const listItems = [];
      // Structures elements as options inside of an unordered list with a listbox role.
      // Search uses this structure with an aria association with the search input without
      // negatively impacting accessibility for other uses of infinite list.
      this.props.listItemProps.forEach((listItem, index) => {
        const key = listItem.itemKey || `index${index}`;
        listItems.push(
          <div
            {...listProperties.accessibleAttributes}
            className={`${listProperties.className}__list__item`}
            key={key}
          >
            <listProperties.listItemComponent
              {...listItem}
              componentName={listProperties.componentName}
            />
          </div>,
        );
      });
      if (
        listProperties.specialLastRow &&
        !this.props.receivedResultsInLastRequest &&
        this.props.hasFetchedData
      ) {
        listItems.push(
          <div
            {...listProperties.accessibleAttributes}
            className={`${listProperties.className}__list__item`}
            key='specialLastRow'
          >
            {listProperties.specialLastRow(this.props.ctaButton)}
          </div>,
        );
      }
      return listItems;
    };

    renderLoading = () => {
      if (
        !listProperties.randomAccessPaging &&
        this.state.isLoading &&
        (!this.props.listItemTotal || this.props.listItemProps.length < this.props.listItemTotal)
      ) {
        return (
          <Loading
            isBabySearch={listProperties.isBabySearch}
            isWhite={this.props.spinnerTheme === SPINNER_WHITE}
            isCentered={listProperties.isSpinnerCentered}
          />
        );
      }
    };

    resetList = resetAction => {
      this.props.dispatch(resetAction());
    };

    reFetchData = (resetAction, forceFetchData = true) => {
      let forceFetch = forceFetchData;
      if (resetAction) {
        this.resetList(resetAction);
        forceFetch = true;
      }
      this.fetchData(forceFetch);
    };

    handleScroll = el => {
      const y = this.props.containerHeight ? el.scrollTop : window.pageYOffset;
      const innerHeight = this.props.containerHeight
        ? this.props.containerHeight
        : window.innerHeight;
      const firstVisibleIndex = Math.floor(y / listProperties.listItemHeight);
      const lastVisibleIndex =
        firstVisibleIndex + Math.floor(innerHeight / listProperties.listItemHeight) - 2;
      const currentVisibleIndex =
        Math.floor(firstVisibleIndex / listProperties.API_ITEM_LIMIT) *
        listProperties.API_ITEM_LIMIT;
      if (this.props.onCurrentViewedIndexChange) {
        this.props.onCurrentViewedIndexChange(currentVisibleIndex);
      }
      this.setState({
        firstVisibleIndex,
        lastVisibleIndex,
      });
    };

    render() {
      if (!this.props.listItemProps) {
        return null;
      }
      let containerHeight = this.props.containerHeight;
      if (
        this.props.listItemProps.length !== 0 &&
        this.props.containerHeight > this.props.listItemProps.length * listProperties.listItemHeight
      ) {
        containerHeight = this.props.listItemProps.length * listProperties.listItemHeight;
      }
      let handleScroll = noop;
      if (listProperties.randomAccessPaging) {
        handleScroll = debounce(this.handleScroll, RANDOM_ACCESS_TIMEOUT);
      } else if (this.props.onScroll) {
        handleScroll = throttle(this.props.onScroll, 5000);
      }

      const infiniteList = (
        <Infinite
          className={`${listProperties.className}__list`}
          containerHeight={containerHeight}
          elementHeight={listProperties.listItemHeight}
          infiniteLoadBeginEdgeOffset={200}
          onInfiniteLoad={this.fetchData}
          loadingSpinnerDelegate={this.renderLoading()}
          isInfiniteLoading={this.state.isLoading}
          handleScroll={handleScroll}
          preloadAdditionalHeight={Infinite.containerHeightScaleFactor(2)}
          useWindowAsScrollContainer={!this.props.containerHeight}
        >
          {this.renderListItems()}
        </Infinite>
      );

      return (
        <Component
          resetList={this.resetList}
          {...this.props}
          infiniteList={infiniteList}
          firstVisibleIndex={this.state.firstVisibleIndex}
          reFetchData={this.reFetchData}
        />
      );
    }
  };

  return connect()(Wrapped);
}
